import { nextTick, onBeforeUnmount, onMounted } from 'vue'
import { gsap, ScrollTrigger } from 'gsap/all'
import { breakpoints } from '~/config'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const GENERATED_CONNECTOR_ATTR = 'data-generated-segment-connectors'
const SEGMENT_PROFILE_ATTR = 'data-segment-profile'
const REVERSE_ANCHOR_ATTR = 'data-anchor-reverse'
const CONNECTOR_FROM_ORDER_ATTR = 'data-connector-from-order'
const CONNECTOR_TO_ORDER_ATTR = 'data-connector-to-order'
const CONNECTOR_TERMINAL_TARGET_ATTR = 'data-connector-terminal-target'

const getAttrTrimmed = (element, attrName) => {
	const rawValue = element.getAttribute(attrName)
	if (typeof rawValue !== 'string') return ''
	return rawValue.trim()
}

const parseBooleanAttribute = (rawValue) => {
	if (typeof rawValue !== 'string') return false

	const normalized = rawValue.trim().toLowerCase()
	return normalized === '' || normalized === 'true' || normalized === '1' || normalized === 'yes'
}

const matchesProfile = (element, attrName, activeProfile) => {
	if (!activeProfile) return false

	const rawProfile = getAttrTrimmed(element, attrName)
	if (!rawProfile) return true

	return rawProfile.toLowerCase() === activeProfile
}

const isDisplayVisible = (element) => {
	if (!(element instanceof Element)) return false

	const style = window.getComputedStyle(element)
	return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse'
}

const isElementVisible = (element) => {
	if (!(element instanceof Element)) return false
	if (!isDisplayVisible(element)) return false

	const rect = element.getBoundingClientRect()
	return rect.width > 0 && rect.height > 0
}

const resolvePathLength = (path) => {
	if (!(path instanceof SVGPathElement)) return null
	if (!isDisplayVisible(path)) return null

	try {
		const totalLength = path.getTotalLength()
		if (!Number.isFinite(totalLength) || totalLength <= 0) return null
		return totalLength
	} catch {
		return null
	}
}

const clearPathDashStyles = (path) => {
	if (!(path instanceof SVGPathElement)) return
	path.style.removeProperty('stroke-dasharray')
	path.style.removeProperty('stroke-dashoffset')
}

export const useSegmentDrawOnScroll = ({
	rootRef,
	minDesktopWidth = breakpoints.xsmall + 1,
	profileResolver = () => (window.innerWidth >= minDesktopWidth ? 'desktop' : 'mobile'),
	enabledProfiles = ['desktop'],
	segmentSelector = '[data-segment-order]',
	segmentProfileAttr = SEGMENT_PROFILE_ATTR,
	connectorPathSelector = `[${GENERATED_CONNECTOR_ATTR}] path`,
	connectorFromOrderAttr = CONNECTOR_FROM_ORDER_ATTR,
	connectorToOrderAttr = CONNECTOR_TO_ORDER_ATTR,
	connectorTerminalTargetAttr = CONNECTOR_TERMINAL_TARGET_ATTR,
	reverseAnchorAttr = REVERSE_ANCHOR_ATTR,
	start = 'top bottom',
	end = 'bottom top',
	scrub = true,
} = {}) => {
	let isDestroyed = false
	let refreshRafId = null
	let drawTimeline = null
	let reducedMotionQuery = null
	let trackedPaths = []

	const getRoot = () => (rootRef?.value instanceof HTMLElement ? rootRef.value : null)

	const clearTrackedPathStyles = () => {
		if (!trackedPaths.length) return
		trackedPaths.forEach((path) => clearPathDashStyles(path))
		trackedPaths = []
	}

	const killDrawTimeline = () => {
		if (!drawTimeline) return

		drawTimeline.scrollTrigger?.kill()
		drawTimeline.kill()
		drawTimeline = null
	}

	const shouldReduceMotion = () => {
		if (!import.meta.client) return false
		return !!reducedMotionQuery?.matches
	}

	const collectSegmentEntries = (root, activeProfile) => {
		return Array.from(root.querySelectorAll(segmentSelector))
			.filter((element) => element instanceof SVGSVGElement)
			.filter((segment) => isElementVisible(segment))
			.filter((segment) => matchesProfile(segment, segmentProfileAttr, activeProfile))
			.map((segment) => {
				const order = Number.parseFloat(segment.getAttribute('data-segment-order') || '')
				const reverse = parseBooleanAttribute(segment.getAttribute(reverseAnchorAttr))
				const paths = Array.from(segment.querySelectorAll('path'))
					.filter((path) => path instanceof SVGPathElement)
					.map((path) => ({
						path,
						length: resolvePathLength(path),
					}))
					.filter((entry) => Number.isFinite(entry.length))

				return { segment, order, reverse, paths }
			})
			.filter((entry) => Number.isFinite(entry.order))
			.sort((a, b) => a.order - b.order)
	}

	const collectConnectorEntriesByFromOrder = (root) => {
		const connectorsByFromOrder = new Map()
		const connectorPaths = Array.from(root.querySelectorAll(connectorPathSelector)).filter((path) => path instanceof SVGPathElement).filter((path) => isDisplayVisible(path))

		for (const path of connectorPaths) {
			const length = resolvePathLength(path)
			if (!Number.isFinite(length)) continue

			const fromOrder = Number.parseFloat(path.getAttribute(connectorFromOrderAttr) || '')
			if (!Number.isFinite(fromOrder)) continue

			const toOrderRaw = Number.parseFloat(path.getAttribute(connectorToOrderAttr) || '')
			const toOrder = Number.isFinite(toOrderRaw) ? toOrderRaw : null
			const terminalTarget = getAttrTrimmed(path, connectorTerminalTargetAttr)

			const entries = connectorsByFromOrder.get(fromOrder) || []
			entries.push({
				path,
				length,
				reverse: false,
				toOrder,
				terminalTarget,
			})
			connectorsByFromOrder.set(fromOrder, entries)
		}

		connectorsByFromOrder.forEach((entries, order) => {
			const sortedEntries = [...entries].sort((a, b) => {
				const aRank = Number.isFinite(a.toOrder) ? 0 : 1
				const bRank = Number.isFinite(b.toOrder) ? 0 : 1
				if (aRank !== bRank) return aRank - bRank
				if (Number.isFinite(a.toOrder) && Number.isFinite(b.toOrder) && a.toOrder !== b.toOrder) return a.toOrder - b.toOrder

				return a.terminalTarget.localeCompare(b.terminalTarget)
			})
			connectorsByFromOrder.set(order, sortedEntries)
		})

		return connectorsByFromOrder
	}

	const buildDrawChain = (segmentEntries, connectorsByFromOrder) => {
		const chain = []

		for (const segmentEntry of segmentEntries) {
			segmentEntry.paths.forEach((pathEntry) => {
				chain.push({
					path: pathEntry.path,
					length: pathEntry.length,
					reverse: segmentEntry.reverse,
				})
			})

			const connectorEntries = connectorsByFromOrder.get(segmentEntry.order) || []
			connectorEntries.forEach((entry) => {
				chain.push({
					path: entry.path,
					length: entry.length,
					reverse: false,
				})
			})
		}

		return chain.filter((entry) => entry.path instanceof SVGPathElement && Number.isFinite(entry.length) && entry.length > 0)
	}

	const refresh = () => {
		if (!import.meta.client || isDestroyed) return

		const root = getRoot()
		if (!root) return

		killDrawTimeline()
		clearTrackedPathStyles()

		const resolvedProfile = profileResolver?.({ root, width: window.innerWidth, minDesktopWidth })
		const activeProfile = typeof resolvedProfile === 'string' ? resolvedProfile.trim().toLowerCase() : ''
		const profileSet = new Set((Array.isArray(enabledProfiles) ? enabledProfiles : []).map((profile) => (typeof profile === 'string' ? profile.trim().toLowerCase() : '')).filter(Boolean))

		if (!activeProfile || !profileSet.size || !profileSet.has(activeProfile)) return

		const segmentEntries = collectSegmentEntries(root, activeProfile)
		if (!segmentEntries.length) return

		const connectorsByFromOrder = collectConnectorEntriesByFromOrder(root)
		const drawChain = buildDrawChain(segmentEntries, connectorsByFromOrder)
		if (!drawChain.length) return

		trackedPaths = Array.from(new Set(drawChain.map((entry) => entry.path)))
		if (shouldReduceMotion()) {
			trackedPaths.forEach((path) => clearPathDashStyles(path))
			return
		}

		for (const entry of drawChain) {
			const startOffset = entry.reverse ? -entry.length : entry.length
			const dashValue = `${entry.length} ${entry.length}`
			gsap.set(entry.path, {
				strokeDasharray: dashValue,
				strokeDashoffset: startOffset,
			})
		}

		const firstSegment = segmentEntries[0]?.segment
		const lastSegment = segmentEntries[segmentEntries.length - 1]?.segment
		if (!(firstSegment instanceof Element) || !(lastSegment instanceof Element)) return

		drawTimeline = gsap.timeline({
			defaults: { ease: 'none' },
			scrollTrigger: {
				trigger: firstSegment,
				endTrigger: lastSegment,
				start,
				end,
				scrub,
				invalidateOnRefresh: true,
			},
		})

		drawChain.forEach((entry) => {
			drawTimeline.to(entry.path, {
				strokeDashoffset: 0,
				duration: entry.length,
			})
		})
	}

	const scheduleRefresh = () => {
		if (!import.meta.client || isDestroyed) return
		if (refreshRafId) cancelAnimationFrame(refreshRafId)
		refreshRafId = requestAnimationFrame(() => {
			refreshRafId = null
			refresh()
		})
	}

	const onWindowResize = () => {
		scheduleRefresh()
	}

	const onReducedMotionChange = () => {
		scheduleRefresh()
	}

	const destroy = () => {
		if (!import.meta.client || isDestroyed) return
		isDestroyed = true

		if (refreshRafId) {
			cancelAnimationFrame(refreshRafId)
			refreshRafId = null
		}

		window.removeEventListener('resize', onWindowResize)
		window.removeEventListener('orientationchange', onWindowResize)
		reducedMotionQuery?.removeEventListener?.('change', onReducedMotionChange)
		reducedMotionQuery?.removeListener?.(onReducedMotionChange)
		reducedMotionQuery = null

		killDrawTimeline()
		clearTrackedPathStyles()
	}

	onMounted(() => {
		if (!import.meta.client || isDestroyed) return

		window.addEventListener('resize', onWindowResize, { passive: true })
		window.addEventListener('orientationchange', onWindowResize, { passive: true })
		reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
		reducedMotionQuery?.addEventListener?.('change', onReducedMotionChange)
		reducedMotionQuery?.addListener?.(onReducedMotionChange)

		nextTick().then(() => {
			scheduleRefresh()
			document.fonts?.ready?.then(() => scheduleRefresh())
		})
	})

	onBeforeUnmount(() => {
		destroy()
	})

	return {
		refresh,
		destroy,
	}
}
